import type { DebugProblem } from "@reflag/shared/eventAPI";

export function formatProblemCode(code: string): string {
  const label = code.toLowerCase().replaceAll("_", " ");
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function getProblemHeadline(problem: DebugProblem): string {
  const context = [problem.field, problem.operator].filter(Boolean).join(" ");
  return `${formatProblemCode(problem.code)}: ${context}`;
}

export function getProblemGuidance(
  problem: Pick<DebugProblem, "code" | "field" | "operator">,
): { explanation: string; fixes: string[] } {
  switch (problem.code) {
    case "MISSING_CONTEXT_FIELD":
      return {
        explanation: `A targeting rule referenced ${problem.field}, but the field was absent from the evaluation context.`,
        fixes: [
          `Include ${problem.field} in the context supplied to the SDK before evaluating the flag. Server-side SDKs evaluate locally and require the full context because they cannot access stored attributes; browser-based SDKs can also use user and company attributes stored on Reflag servers. Stored attributes take a few seconds to process, so attributes sent from your server may not be immediately available to the client.`,
          `Alternatively, update the targeting condition that references ${problem.field}.`,
          `If ${problem.field} can be missing, guard the targeting condition with ${problem.field} IS_SET first in the same AND group. Evaluation will short-circuit when the field is missing, skipping the remaining conditions and avoiding missing-field errors.`,
        ],
      };
    case "UNSUPPORTED_ARRAY_OPERATOR":
      if (problem.operator === "rolloutPercentage") {
        return {
          explanation: `A percentage rollout used ${problem.field}, but the field was supplied as an array. Percentage rollouts require a scalar value.`,
          fixes: [
            "Choose a stable scalar field, such as user.id or company.id, as the percentage rollout attribute.",
            `Alternatively, supply a stable scalar value for ${problem.field} if that matches your intended data model.`,
          ],
        };
      }
      return {
        explanation: `${problem.field} was supplied as an array, but the evaluating SDK does not support ${problem.operator ?? "this operator"} for arrays.`,
        fixes: [
          `Supply a scalar value for ${problem.field} if that matches your intended data model.`,
          "Otherwise, choose an array-compatible targeting condition supported by your SDK version. Check whether upgrading your SDK adds support for the intended operator.",
        ],
      };
    default:
      return {
        explanation:
          "The SDK reported a targeting evaluation diagnostic. Inspect the event below for the full diagnostic and evaluation context.",
        fixes: [
          "Review the related flag's targeting conditions and the context supplied to the SDK.",
        ],
      };
  }
}
